import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Controller, useFormContext, useFormState } from "react-hook-form";
import DOMPurify from "dompurify";
import { Title } from "../../../common/typography/Title";
import styled from "styled-components";
import { ArrowDown, Completed, NonCompleted } from "../../icons";
import { BREAKPOINT_RESERVATION_PAGE } from "../../../pageComponents/reservationPage/constants/breakpoints";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  styled as muiStyled,
} from "@mui/material";
import { colors } from "../../common/mixins";
import {
  ItemContent,
  ItemTitle,
  TicketSection,
  TicketNameWithDiscount,
} from "../order-info";
import { Products } from "../products/products";
import {
  CountryCodeDropdown,
  DsAutocomplete,
  DsTextField,
  FullWidth,
} from "../../../common";
import {
  PickListOptions,
  Ticket,
  TicketPersonFormField,
} from "../../../types/reservation";
import { useReservationStore } from "../../../stores/useReservationStore";
import { DataTypeCode } from "../../../common/enums/DataTypeCode";
import { useCountryCodes } from "../../../pageComponents/reservationPage/hooks/useCountryCodes";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import "dayjs/locale/el";
import WarningInfo from "../../order-details/WarningInfo";
import { DateField } from "@mui/x-date-pickers/DateField";
import { DsCheckbox } from "../../../common/inputs/DsCheckbox";
import { CountryCodePrefix, PhoneAdornment } from "../phone-number-field";
import { useReservationResources } from "../../../pageComponents/reservationPage/hooks/useReservationResources";
import { useCountriesOptions } from "../../../pageComponents/reservationPage/hooks/useCountriesOptions";
import { endpoints } from "../../../utils/Constants";
import { filterNameInput } from "../../../utils/Utils";
import {
  isTicketPersonComplete,
  isKokAgeEligible,
  getEagerFieldAction,
  isOlympiacosFancardWaived,
  KOK_AGE_CONDITIONAL_MAPS,
} from "../../../pageComponents/reservationPage/utils/ticketPersonUtils";
import { useNationalityCertPattern } from "../../../pageComponents/reservationPage/hooks/useNationalityCertPattern";

import { Section } from "../../../pageComponents/reservation-shared-styles";
import { MIN_DATE_YEAR, MAX_DATE_YEAR, PARTIAL_DATE_SUFFIX } from "../../../pageComponents/reservationPage/reservation-page.schema";

dayjs.extend(customParseFormat);

// Bounds mirror validateDateField in reservation-page.schema.ts.
const DATE_FIELD_MIN_DATE = dayjs(`01-01-${MIN_DATE_YEAR}`, "DD-MM-YYYY");
const DATE_FIELD_MAX_DATE = dayjs(`31-12-${MAX_DATE_YEAR}`, "DD-MM-YYYY");

// True once a date field's raw text has at least one digit but still shows at
// least one section placeholder (e.g. "DD/01/8888") — i.e. it's neither empty
// nor complete. MUI's DateField never surfaces this state through its value/
// onChange API (it withholds onChange entirely until every section is filled).
function isPartiallyFilledDateText(text: string): boolean {
  return /\d/.test(text) && /\p{L}/u.test(text);
}

// ─── Styled components ────────────────────────────────────────────────────────

const MuiAccordion = muiStyled(Accordion)`
  box-shadow: none;
  margin: 0;
  padding: 16px;
  border-radius: 8px !important;
  border: 1px solid ${colors.lightGray};

  &.Mui-expanded {
    margin: 0;
    padding-bottom: 16px;
  }
`;

const MuiAccordionSummary = muiStyled(AccordionSummary)`
  padding: 0;

  &.Mui-expanded {
    min-height: 48px;
  }

  .MuiAccordionSummary-content {
    display: flex;
    gap: 8px;
    margin: 0;
    align-items: center;
    min-width: 0;

    &.Mui-expanded {
      margin: 0;
    }
  }
`;

const MuiAccordionDetails = muiStyled(AccordionDetails)`
  padding: 0;
`;



const renderErrorContent = (message: string | undefined): React.ReactNode =>
    message ? (
        <span
            dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(message, { ADD_ATTR: ["target"] }),
            }}
        />
    ) : undefined;

const AccordionTitleContainer = styled.div`
  min-width: 0;
  overflow: hidden;
  flex: 1;
`;

const StatusIcon = styled.span`
  display: flex;
  @media (max-width: ${BREAKPOINT_RESERVATION_PAGE}) {
    svg {
      width: 24px;
      height: 24px;
    }
  }
`;

const PhoneRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  padding-top: 16px;
`;

const AccordionBody = styled.div<{ $hasFields: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  /* The expanded summary is min-height 40px around 24px of text, so it already
     contributes 8px below the ticket description. Grid's own padding-top covers
     the rest when fields exist; a products-only ticket has no Grid, so make up
     the difference here to keep the gap at 16px either way. */
  ${({ $hasFields }) => !$hasFields && "padding-top: 8px;"}
`;

// Stable primitives (ticketId, currency) keep a keystroke-driven TicketInfo
// re-render from propagating into the carousel.
const MemoProducts = React.memo(Products);

// ─── Accordion expansion hook ─────────────────────────────────────────────────

function useAccordionExpansion(tickets: Ticket[]) {
  const [expandedIds, setExpandedIds] = useState<Set<number>>(() => {
    const s = new Set<number>();
    if (tickets[0]?.id != null) s.add(tickets[0].id);
    return s;
  });

  // Kept in sync each render so toggle() always reads fresh state without
  // needing to be recreated (avoids stale-closure in useCallback).
  const expandedIdsRef = useRef(expandedIds);
  expandedIdsRef.current = expandedIds;

  // Tickets the user explicitly closed — auto-expand won't reopen them.
  const manuallyClosedIdsRef = useRef(new Set<number>());
  // Initialized to current tickets so the first effect run sees no diff.
  const prevTicketsRef = useRef(tickets);

  // Auto-expand newly selected tickets; remove deselected ones without
  // disturbing any other accordion.
  useEffect(() => {
    const prevIds = new Set(prevTicketsRef.current.map((t) => t.id));
    const added = tickets.filter((t) => !prevIds.has(t.id));
    const removedIds = prevTicketsRef.current
      .filter((t) => !tickets.some((cur) => cur.id === t.id))
      .map((t) => t.id);
    prevTicketsRef.current = tickets;

    if (added.length === 0 && removedIds.length === 0) return;

    removedIds.forEach((id) => manuallyClosedIdsRef.current.delete(id));
    setExpandedIds((prev) => {
      const next = new Set(prev);
      removedIds.forEach((id) => next.delete(id));
      added.forEach((t) => {
        if (!manuallyClosedIdsRef.current.has(t.id)) next.add(t.id);
      });
      return next;
    });
  }, [tickets]);

  const toggle = useCallback((id: number) => {
    const isExpanded = expandedIdsRef.current.has(id);
    if (isExpanded) {
      manuallyClosedIdsRef.current.add(id);
    } else {
      manuallyClosedIdsRef.current.delete(id);
    }
    setExpandedIds((prev) => {
      const next = new Set(prev);
      isExpanded ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  // Force-expand (e.g. on validation errors) — ignores manual-close intent.
  const expand = useCallback((ids: number[]) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });
  }, []);

  // Expand only if the user hasn't manually closed the accordion.
  const expandIfNotManuallyClosed = useCallback((ids: number[]) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      ids
        .filter((id) => !manuallyClosedIdsRef.current.has(id))
        .forEach((id) => next.add(id));
      return next;
    });
  }, []);

  return { expandedIds, toggle, expand, expandIfNotManuallyClosed };
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface TicketInfoProps {
  ticketPersonFormFields: TicketPersonFormField[];
  tickets: Ticket[];
  isKokEvent?: boolean;
  isOlympiacosEvent?: boolean;
  hasPerTicketProducts: boolean;
  currency: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function TicketInfo({
  ticketPersonFormFields,
  tickets,
  isKokEvent = false,
  isOlympiacosEvent = false,
  hasPerTicketProducts,
  currency,
}: TicketInfoProps) {
  const formData = useReservationStore((s) => s.ticketPersonData);
  const setTicketPersonField = useReservationStore((s) => s.setTicketPersonField);
  const { trigger, setValue, control, clearErrors } = useFormContext();
  const { errors, submitCount } = useFormState({ control });
  const ticketPersonErrors = (errors as any)?.ticketPersonData;
  const { countryCodes, defaultCountry } = useCountryCodes();
  const r = useReservationResources();

  const countryField = useMemo(
    () => ticketPersonFormFields.find((f) => f.ticketPersonMap === "Country"),
    [ticketPersonFormFields],
  );
  const cityField = useMemo(
    () => ticketPersonFormFields.find((f) => f.ticketPersonMap === "City"),
    [ticketPersonFormFields],
  );

  const lang = useMemo(() => {
    const culture = (document.getElementById("lng") as HTMLInputElement)?.value ?? "";
    return culture.split("-")[0].toLowerCase() === "el" ? "el" : "en";
  }, []);

  const countryOptions = useCountriesOptions(!!countryField);
  const [cityOptionsMap, setCityOptionsMap] = useState<Record<number, { GeolocationId: number; Name: string }[]>>({});
  const cityFetchedTicketsRef = useRef<Set<number>>(new Set());

  const setField = useCallback(
    (ticketId: number, fieldKey: string, value: string | null) => {
      setTicketPersonField(ticketId, fieldKey, value);
      setValue(`ticketPersonData.${ticketId}.${fieldKey}` as any, value, {
        shouldValidate: false,
      });
    },
    [setTicketPersonField, setValue],
  );

  // EVP-11038: AMKA uniqueness is a cross-ticket rule, so revalidating only the
  // edited field leaves stale duplicate errors on its sibling once the
  // duplicate is resolved. Trigger validation on every AMKA-bearing field
  // (dedicated AMKA field, plus CertificationNumber regardless of cert type —
  // RHF skips non-AMKA values cheaply) across all tickets.
  const triggerAmkaSiblings = useCallback((excludeTicketId?: number) => {
    const certNumberField = ticketPersonFormFields.find(
      (f) => f.ticketPersonMap === "CertificationNumber",
    );
    const amkaField = ticketPersonFormFields.find(
      (f) => f.ticketPersonMap === "Amka",
    );
    if (!certNumberField && !amkaField) return;
    const paths: string[] = [];
    for (const ticket of tickets) {
      if (ticket.id === excludeTicketId) continue;
      if (certNumberField)
        paths.push(
          `ticketPersonData.${ticket.id}.${certNumberField.dynamicAttributeId}`,
        );
      if (amkaField)
        paths.push(
          `ticketPersonData.${ticket.id}.${amkaField.dynamicAttributeId}`,
        );
    }
    if (paths.length) trigger(paths as any);
  }, [ticketPersonFormFields, tickets, trigger]);

  const isAmkaBearingField = useCallback(
    (ticketId: number, field: TicketPersonFormField): boolean => {
      if (field.ticketPersonMap === "Amka") return true;
      if (field.ticketPersonMap === "CertificationNumber") {
        const certTypeField = ticketPersonFormFields.find(
          (f) => f.ticketPersonMap === "TypeOfCertificate",
        );
        if (!certTypeField) return false;
        return (
          formData[ticketId]?.[certTypeField.dynamicAttributeId] === "5"
        );
      }
      return false;
    },
    [ticketPersonFormFields, formData],
  );

  const { expandedIds, toggle, expand, expandIfNotManuallyClosed } =
    useAccordionExpansion(tickets);

  // Auto-expand all accordions that have a field error.
  useEffect(() => {
    if (!ticketPersonErrors) return;
    const errorIds = tickets
      .filter((t) => ticketPersonErrors[String(t.id)])
      .map((t) => t.id);
    if (errorIds.length > 0) expand(errorIds);
  }, [ticketPersonErrors, tickets, expand]);

  const [dateValues, setDateValues] = useState<
    Record<string, dayjs.Dayjs | null>
  >({});
  const [dateErrors, setDateErrors] = useState<Record<string, string | null>>(
    {},
  );

  // KOK: the Age field definition and the fields that are gated behind age >= 14
  const kokAgeField = useMemo(
    () =>
      isKokEvent
        ? (ticketPersonFormFields.find((f) => f.ticketPersonMap === "Age") ?? null)
        : null,
    [isKokEvent, ticketPersonFormFields],
  );
  const kokConditionalFields = useMemo(
    () =>
      isKokEvent
        ? ticketPersonFormFields.filter(
            (f) =>
              f.ticketPersonMap &&
              KOK_AGE_CONDITIONAL_MAPS.has(f.ticketPersonMap),
          )
        : [],
    [isKokEvent, ticketPersonFormFields],
  );

  const getIsKokAgeEligible = (ticketId: number): boolean => {
    if (!isKokEvent || !kokAgeField) return true;
    return isKokAgeEligible(ticketId, formData, kokAgeField.dynamicAttributeId);
  };

  // Olympiacos: zero-price and kids-discount tickets waive the fancard, so the field is hidden
  const fancardField = useMemo(
    () =>
      isOlympiacosEvent
        ? (ticketPersonFormFields.find((f) => f.ticketPersonMap === "SportsCard") ?? null)
        : null,
    [isOlympiacosEvent, ticketPersonFormFields],
  );

  // The fancard field is hidden on waived tickets, so drop whatever it held (e.g. a
  // prefilled card) — otherwise a card the user cannot see stays in the payload,
  // along with any error it had.
  useEffect(() => {
    if (!fancardField) return;
    for (const ticket of tickets) {
      if (!formData[ticket.id]?.[fancardField.dynamicAttributeId]) continue;
      if (!isOlympiacosFancardWaived(ticket, isOlympiacosEvent)) continue;

      setField(ticket.id, fancardField.dynamicAttributeId, null);
      clearErrors(
        `ticketPersonData.${ticket.id}.${fancardField.dynamicAttributeId}` as any,
      );
    }
  }, [
    fancardField,
    tickets,
    formData,
    isOlympiacosEvent,
    setField,
    clearErrors,
  ]);

  const {
    displayFields,
    getCertOptions,
    onNationalityChange,
  } = useNationalityCertPattern(ticketPersonFormFields);

  // Auto-select when a dropdown is reduced to a single option.
  // Tracks what was auto-selected so user clears are respected; re-fires
  // only when the available option itself changes (e.g. nationality switch).
  const autoSelectedRef = useRef<Record<string, string>>({});
  useEffect(() => {
    for (const ticket of tickets) {
      for (const field of displayFields) {
        if (field.dataTypeCode !== DataTypeCode.dropdown) continue;
        const options = getCertOptions(ticket.id, field);
        const key = ticket.id + "-" + field.dynamicAttributeId;
        if (options.length !== 1) {
          delete autoSelectedRef.current[key];
          continue;
        }
        const singleOption = options[0].optionValue;
        // Same option already auto-selected: respect any user clear
        if (autoSelectedRef.current[key] === singleOption) continue;
        const currentValue = formData[ticket.id]?.[field.dynamicAttributeId];
        if (!currentValue) {
          setField(ticket.id, field.dynamicAttributeId, singleOption);
          autoSelectedRef.current[key] = singleOption;
          if (submitCount > 0)
            trigger(
              `ticketPersonData.${ticket.id}.${field.dynamicAttributeId}` as any,
            );
        }
      }
    }
  }, [tickets, displayFields, formData]);

  // When a ticket is selected, populate server-provided values into the
  // store + RHF (only if not already set), then expand any accordion that has
  // at least one value — whether freshly written or previously stored.
  useEffect(() => {
    const idsToExpand: number[] = [];
    const newDateValues: Record<string, dayjs.Dayjs | null> = {};
    for (const ticket of tickets) {
      for (const ff of ticket.formFields ?? []) {
        if (!ff.value || formData[ticket.id]?.[ff.id]) continue;
        const fieldDef = ticketPersonFormFields.find(
          (f) => f.dynamicAttributeId === ff.id,
        );
        if (cityField && ff.id === cityField.dynamicAttributeId) {
          // City is validated against the cities API response — handled in the
          // countryOptions useEffect after the fetch completes.
          continue;
        } else if (fieldDef?.dataTypeCode === DataTypeCode.date) {
          const parsed = dayjs(ff.value);
          if (parsed.isValid()) {
            newDateValues[`${ticket.id}-${ff.id}`] = parsed;
            setField(ticket.id, ff.id, parsed.format("DD-MM-YYYY"));
          }
        } else {
          setField(ticket.id, ff.id, ff.value);
        }
      }
      const hasAnyValue = (ticket.formFields ?? []).some(
        (ff) => ff.value || formData[ticket.id]?.[ff.id],
      );
      if (hasAnyValue) idsToExpand.push(ticket.id);
    }
    if (Object.keys(newDateValues).length > 0)
      setDateValues((prev) => ({ ...prev, ...newDateValues }));
    if (idsToExpand.length > 0) expandIfNotManuallyClosed(idsToExpand);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets]);


  useEffect(() => {
    if (!countryOptions.length || !countryField || !cityField) return;
    tickets.forEach((ticket) => {
      if (cityFetchedTicketsRef.current.has(ticket.id)) return;
      const countryFf = (ticket.formFields ?? []).find(
        (ff) => ff.id === countryField.dynamicAttributeId,
      );
      // Prefer formData (user's current selection) over the original server value so
      // that a country change survives TicketInfo unmount/remount (e.g. all tickets
      // deselected then reselected). Falls back to ticket.formFields for the initial
      // mount before the init effect has populated formData.
      const storedCode =
        formData[ticket.id]?.[countryField.dynamicAttributeId] ||
        countryFf?.value;
      if (!storedCode) return;
      cityFetchedTicketsRef.current.add(ticket.id);
      fetch(endpoints.common.getCities(storedCode, lang))
        .then((r) => {
          if (!r.ok) throw new Error(`cities fetch failed: ${r.status}`);
          return r.json();
        })
        .then((cities) => {
          setCityOptionsMap((prev) => ({ ...prev, [ticket.id]: cities }));
          // Set pre-filled city value after options are loaded so the
          // dropdown can resolve it to a valid option.
          const cityFf = (ticket.formFields ?? []).find(
            (ff) => ff.id === cityField.dynamicAttributeId,
          );
          const cityFfValue = cityFf?.value ?? null;
          const cityValueValid =
            !!cityFfValue &&
            cities.some(
              (c: { GeolocationId: number; Name: string }) =>
                String(c.GeolocationId) === cityFfValue,
            );
          // Only pre-fill if not already set — preserves session-restored values
          // and any city the user picked manually before a payment redirect.
          if (cityValueValid && !formData[ticket.id]?.[cityField.dynamicAttributeId]) {
            setField(ticket.id, cityField.dynamicAttributeId, cityFfValue);
          }
        })
        .catch((err) => {
          console.error(err);
          cityFetchedTicketsRef.current.delete(ticket.id);
        });
    });
  }, [countryOptions, tickets]);

  const getTicketDisplayFields = useCallback(
    (ticket: Ticket): TicketPersonFormField[] => {
      if (!ticket.formFields?.length) return [];
      const ids = new Set(ticket.formFields.map((f) => f.id));
      return displayFields.filter((f) => ids.has(f.dynamicAttributeId));
    },
    [displayFields],
  );

  const isTicketFormComplete = useCallback(
    (ticket: Ticket, fields: TicketPersonFormField[]) =>
      isTicketPersonComplete(
        ticket,
        fields,
        formData,
        isKokEvent,
        kokAgeField?.dynamicAttributeId,
        isOlympiacosEvent,
      ),
    [formData, isKokEvent, kokAgeField, isOlympiacosEvent],
  );

  // ─── Field renderers ──────────────────────────────────────────────────────

  const renderPhoneField = (ticket: Ticket, field: TicketPersonFormField) => {
    const { id: ticketId } = ticket;
    const fieldKey = field.dynamicAttributeId;
    const fieldPath = `ticketPersonData.${ticketId}.${fieldKey}` as any;

    return (
      <Controller
        control={control}
        name={fieldPath}
        render={({ field: rhfField, fieldState }) => {
          // CC is stored as the first space-separated token: "+30 69000000"
          const storedValue = rhfField.value ?? "";
          const [storedCc, ...rest] = storedValue.split(" ");
          const hasCcPrefix = storedCc.startsWith("+");
          const currentCc = hasCcPrefix
            ? storedCc
            : `+${countryCodes.find((o) => o.code === defaultCountry)?.prefix ?? ""}`;
          // If no "+CC " prefix, the entire stored value is the phone number (server-provided plain digits)
          const phoneNumber = hasCcPrefix ? rest.join(" ") : storedValue;
          const selectedOpt = countryCodes.find((o) => `+${o.prefix}` === currentCc) ?? null;

          const updateValue = (cc: string, phone: string) => {
            const newValue = `${cc} ${phone}`;
            rhfField.onChange(newValue);
            setTicketPersonField(ticketId, fieldKey, newValue);
          };

          return (
            <PhoneRow>
              <CountryCodeDropdown
                options={countryCodes}
                value={selectedOpt}
                defaultValue={defaultCountry}
                onChange={(opt) => updateValue(`+${opt.prefix}`, phoneNumber)}
              />
              <DsTextField
                type="tel"
                label={field.attributeName}
                required={field.required}
                value={phoneNumber}
                onChange={(e) => {
                  e.target.value = e.target.value.replace(/\D/g, "");
                  updateValue(currentCc, e.target.value);
                  if (fieldState.error) {
                    if (e.target.value) trigger(fieldPath);
                    else clearErrors(fieldPath);
                  }
                }}
                onBlur={() => {
                  if (phoneNumber) trigger(fieldPath);
                }}
                error={!!fieldState.error}
                helperText={renderErrorContent(fieldState.error?.message)}
                InputProps={{
                  startAdornment: currentCc ? (
                    <PhoneAdornment position="start">
                      <CountryCodePrefix>{currentCc}</CountryCodePrefix>
                    </PhoneAdornment>
                  ) : undefined,
                }}
              />
            </PhoneRow>
          );
        }}
      />
    );
  };

  const renderTextField = (
    ticket: Ticket,
    field: TicketPersonFormField,
    type: "text" | "number" = "text",
  ) => {
    const { id: ticketId } = ticket;
    const fieldKey = field.dynamicAttributeId;
    const fieldPath = `ticketPersonData.${ticketId}.${fieldKey}` as any;

    return (
      <Controller
        control={control}
        name={fieldPath}
        render={({ field: rhfField, fieldState }) => {
          const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            let value = e.target.value;
            if (field.ticketPersonMap === "FirstName" || field.ticketPersonMap === "LastName") {
              value = filterNameInput(value);
            }
            if (field.ticketPersonMap === "MemberCard") {
              value = value.toUpperCase().replace(/Μ/g, "M").replace(/Κ/g, "K");
            }
            if (field.ticketPersonMap === "FinancialSupportCoupon" || field.ticketPersonMap === "SportsCard") {
              value = value.toUpperCase();
            }
            rhfField.onChange(value);
            setTicketPersonField(ticketId, fieldKey, value);

            // KOK: clear conditional fields when age drops below 14
            if (isKokEvent && field.ticketPersonMap === "Age") {
              const age = parseInt(value, 10);
              if (isNaN(age) || age < 14) {
                for (const f of kokConditionalFields) {
                  setField(ticketId, f.dynamicAttributeId, null);
                }
              }
            }

            const eagerAction = getEagerFieldAction(field.ticketPersonMap, value, !!fieldState.error);
            if (eagerAction === "trigger") trigger(fieldPath);
            else if (eagerAction === "clear") clearErrors(fieldPath);
            else if (fieldState.error) {
              if (value) trigger(fieldPath);
              else clearErrors(fieldPath);
            }

            // Cross-ticket AMKA uniqueness: revalidate siblings on any change.
            // When clearing, exclude this ticket so clearErrors isn't overwritten
            // by a re-triggered Required error on the now-empty field.
            if (isAmkaBearingField(ticketId, field) && submitCount > 0)
              triggerAmkaSiblings(value ? undefined : ticketId);
          };

          return (
            <DsTextField
              type={type === "number" ? "text" : type}
              inputMode={type === "number" ? "numeric" : undefined}
              name={field.ticketPersonMap}
              label={field.attributeName}
              required={field.required}
              value={rhfField.value ?? ""}
              onChange={handleChange}
              onBlur={() => {
                if (rhfField.value) trigger(fieldPath);
                if (rhfField.value && isAmkaBearingField(ticketId, field) && submitCount > 0) triggerAmkaSiblings();
              }}
              error={!!fieldState.error}
              helperText={renderErrorContent(fieldState.error?.message)}
            />
          );
        }}
      />
    );
  };

  const renderDropdownField = (
    ticket: Ticket,
    field: TicketPersonFormField,
  ) => {
    const { id: ticketId } = ticket;
    const fieldKey = field.dynamicAttributeId;
    const fieldPath = `ticketPersonData.${ticketId}.${fieldKey}` as any;
    const options = getCertOptions(ticketId, field);

    return (
      <Controller
        control={control}
        name={fieldPath}
        render={({ field: rhfField, fieldState }) => (
          <DsAutocomplete
            autoSelectSingleOption
            options={options}
            getOptionLabel={(option: PickListOptions) => option.displayText || ""}
            isOptionEqualToValue={(option, value) => option.optionValue === value.optionValue}
            value={options.find((o) => o.optionValue === rhfField.value) ?? null}
            onChange={(_, newValue: PickListOptions | null) => {
              rhfField.onChange(newValue?.optionValue ?? null);
              setTicketPersonField(ticketId, fieldKey, newValue?.optionValue ?? null);
              if (newValue) {
                if (submitCount > 0 || fieldState.error) trigger(fieldPath);
              } else if (fieldState.error) {
                clearErrors(fieldPath);
              }

              if (field.ticketPersonMap === "Nationality") {
                onNationalityChange(ticketId);
                const certNumberField = ticketPersonFormFields.find(
                  (f) => f.ticketPersonMap === "CertificationNumber",
                );
                if (certNumberField) {
                  clearErrors(`ticketPersonData.${ticketId}.${certNumberField.dynamicAttributeId}` as any);
                }
              }

              if (field.ticketPersonMap === "TypeOfCertificate") {
                const certNumberField = ticketPersonFormFields.find(
                  (f) => f.ticketPersonMap === "CertificationNumber",
                );
                if (certNumberField) {
                  clearErrors(`ticketPersonData.${ticketId}.${certNumberField.dynamicAttributeId}` as any);
                }
                // Toggling AMKA on/off changes the cross-ticket duplicate set —
                // revalidate every AMKA-bearing field so siblings don't show stale
                // duplicate errors. Only after submit to avoid premature errors.
                if (submitCount > 0) triggerAmkaSiblings(ticketId);
              }
            }}
            onBlur={() => {
              if (rhfField.value && (submitCount > 0 || fieldState.error)) trigger(fieldPath);
            }}
            renderInput={(params) => (
              <DsTextField
                {...params}
                name={field.ticketPersonMap || fieldKey}
                label={field.attributeName}
                required={field.required}
                error={!!fieldState.error}
                helperText={renderErrorContent(fieldState.error?.message)}
              />
            )}
          />
        )}
      />
    );
  };

  const renderCheckboxField = (
    ticket: Ticket,
    field: TicketPersonFormField,
  ) => {
    const { id: ticketId } = ticket;
    const fieldKey = field.dynamicAttributeId;
    const fieldPath = `ticketPersonData.${ticketId}.${fieldKey}` as any;

    return (
      <Controller
        control={control}
        name={fieldPath}
        render={({ field: rhfField, fieldState }) => (
          <DsCheckbox
              label={
                <span
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(field.content ?? "", {
                      ADD_ATTR: ["target"],
                    }),
                  }}
                />
              }
              checked={rhfField.value === "true"}
              onChange={() => {
                const newValue = rhfField.value === "true" ? "false" : "true";
                rhfField.onChange(newValue);
                setTicketPersonField(ticketId, fieldKey, newValue);
                if (newValue === "true") trigger(fieldPath);
                else clearErrors(fieldPath);
              }}
              required={field.required}
              error={!!fieldState.error}
              helperText={renderErrorContent(fieldState.error?.message)}
            />
        )}
      />
    );
  };

  const renderDateField = (ticket: Ticket, field: TicketPersonFormField) => {
    const { id: ticketId } = ticket;
    const fieldKey = field.dynamicAttributeId;
    const fieldPath = `ticketPersonData.${ticketId}.${fieldKey}` as any;
    const dateKey = `${ticketId}-${fieldKey}`;

    return (
      <Controller
        control={control}
        name={fieldPath}
        render={({ field: rhfField, fieldState }) => {
          const dateError = dateErrors[dateKey] ?? fieldState.error?.message;

          // If dateValues hasn't been populated yet (e.g. session restore set formData
          // before the init effect ran), derive the display value from RHF directly.
          const dateValue =
            dateValues[dateKey] ??
            (rhfField.value
              ? dayjs(rhfField.value, ["DD-MM-YYYY", "YYYY-MM-DD HH:mm:ss"])
              : null);

          return (
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="el">
              <DateField
                enableAccessibleFieldDOMStructure={false}
                label={field.attributeName}
                value={dateValue}
                minDate={DATE_FIELD_MIN_DATE}
                maxDate={DATE_FIELD_MAX_DATE}
                onChange={(newValue) => {
                  setDateValues((prev) => ({ ...prev, [dateKey]: newValue }));
                  // Always sync to RHF, even for a dayjs-invalid or out-of-range value —
                  // validateDateField (reservation-page.schema.ts) is the single source of
                  // truth for date bounds, so it must actually see whatever was typed.
                  const formatted = newValue ? newValue.format("DD-MM-YYYY") : null;
                  rhfField.onChange(formatted);
                  setTicketPersonField(ticketId, fieldKey, formatted);
                  // The field just became complete (see the comment on onBlur's partial-date
                  // check below), so it can no longer be "partially" filled.
                  if (formatted) setField(ticketId, `${fieldKey}${PARTIAL_DATE_SUFFIX}`, null);
                  // Unlike text fields, DateField's onChange only fires once every section
                  // (day/month/year) has a value — MUI withholds it entirely while a section
                  // is still empty (see useFieldState's updateSectionValue). So a non-null
                  // newValue here already means "the user just finished filling the date",
                  // which is exactly when we want to validate — even for an impossible
                  // combination like day 32, not just once an error is already showing.
                  if (formatted) trigger(fieldPath);
                  else if (fieldState.error) clearErrors(fieldPath);
                }}
                onError={(error) =>
                  setDateErrors((prev) => ({
                    ...prev,
                    [dateKey]: error ? r.InvalidDate : null,
                  }))
                }
                slots={{ textField: DsTextField }}
                slotProps={{
                  textField: {
                    required: field.required,
                    name: field.ticketPersonMap || fieldKey,
                    error: !!dateError,
                    helperText: renderErrorContent(dateError ?? undefined),
                    onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                      // Optional fields never reach the schema's format check while empty
                      // (validateDateField only runs against a truthy value), so a half-typed
                      // date (e.g. only month+year) would otherwise submit silently as blank.
                      // MUI never publishes a value for that state (see onChange above), so the
                      // field's raw text is the only signal — write it to a companion RHF field
                      // (PARTIAL_DATE_SUFFIX) instead of the real one, so the schema can flag it
                      // through the normal resolver-driven error/scroll/expand flow without us
                      // ever handing DateField's own `value` prop something it can't represent
                      // (a day-missing date), which would otherwise wedge the field permanently.
                      if (!field.required) {
                        const partialFieldKey = `${fieldKey}${PARTIAL_DATE_SUFFIX}`;
                        const wasPartial = !!formData[ticketId]?.[partialFieldKey];
                        const isPartial = isPartiallyFilledDateText(e.target.value);
                        setField(ticketId, partialFieldKey, isPartial ? "1" : null);
                        // Only re-validate when the partial flag actually changes (or there's
                        // already a real value/error to re-check) — an untouched empty field
                        // stays empty/not-partial across blur and has nothing new to validate.
                        if (isPartial !== wasPartial || (dateValues[dateKey] ?? rhfField.value) || fieldState.error) {
                          trigger(fieldPath);
                        }
                        return;
                      }
                      if (dateValues[dateKey] ?? rhfField.value) trigger(fieldPath);
                    },
                  },
                }}
              />
            </LocalizationProvider>
          );
        }}
      />
    );
  };

  const renderCountrySelectField = (ticket: Ticket, field: TicketPersonFormField) => {
    const { id: ticketId } = ticket;
    const fieldKey = field.dynamicAttributeId;
    const fieldPath = `ticketPersonData.${ticketId}.${fieldKey}` as any;

    return (
      <Controller
        control={control}
        name={fieldPath}
        render={({ field: rhfField, fieldState }) => {
          const selectedOption = countryOptions.find((c) => c.Code === rhfField.value) ?? null;

          return (
            <DsAutocomplete
              autoSelectSingleOption
              options={countryOptions}
              getOptionLabel={(option: { Code: string; Name: string }) => option.Name}
              isOptionEqualToValue={(option, value) => option.Code === value.Code}
              value={selectedOption}
              onChange={(_, newValue: { Code: string; Name: string } | null) => {
                rhfField.onChange(newValue?.Code ?? null);
                setTicketPersonField(ticketId, fieldKey, newValue?.Code ?? null);
                if (cityField) setField(ticketId, cityField.dynamicAttributeId, null);
                if (newValue) {
                  fetch(endpoints.common.getCities(newValue.Code, lang))
                    .then((r) => r.json())
                    .then((cities) =>
                      setCityOptionsMap((prev) => ({ ...prev, [ticketId]: cities })),
                    )
                    .catch(console.error);
                } else {
                  setCityOptionsMap((prev) => ({ ...prev, [ticketId]: [] }));
                }
                if (newValue) trigger(fieldPath);
                else if (fieldState.error) clearErrors(fieldPath);
              }}
              onBlur={() => {
                if (rhfField.value) trigger(fieldPath);
              }}
              renderInput={(params) => (
                <DsTextField
                  {...params}
                  name={field.ticketPersonMap || fieldKey}
                  label={field.attributeName}
                  required={field.required}
                  error={!!fieldState.error}
                  helperText={renderErrorContent(fieldState.error?.message)}
                  inputProps={{ ...params.inputProps, autoComplete: "new-password" }}
                />
              )}
            />
          );
        }}
      />
    );
  };

  const renderCitySelectField = (ticket: Ticket, field: TicketPersonFormField) => {
    const { id: ticketId } = ticket;
    const fieldKey = field.dynamicAttributeId;
    const fieldPath = `ticketPersonData.${ticketId}.${fieldKey}` as any;
    const options = cityOptionsMap[ticketId] ?? [];

    return (
      <Controller
        control={control}
        name={fieldPath}
        render={({ field: rhfField, fieldState }) => {
          const selectedOption = options.find((c) => String(c.GeolocationId) === rhfField.value) ?? null;

          return (
            <DsAutocomplete
        autoSelectSingleOption
              options={options}
              getOptionLabel={(option: { GeolocationId: number; Name: string }) => option.Name}
              isOptionEqualToValue={(option, value) => option.GeolocationId === value.GeolocationId}
              renderOption={(props, option: { GeolocationId: number; Name: string }) => (
                <li {...props} key={option.GeolocationId}>
                  {option.Name}
                </li>
              )}
              value={selectedOption}
              onChange={(_, newValue: { GeolocationId: number; Name: string } | null) => {
                const newVal = newValue ? String(newValue.GeolocationId) : null;
                rhfField.onChange(newVal);
                setTicketPersonField(ticketId, fieldKey, newVal);
                if (newValue) trigger(fieldPath);
                else if (fieldState.error) clearErrors(fieldPath);
              }}
              onBlur={() => {
                if (rhfField.value) trigger(fieldPath);
              }}
              renderInput={(params) => (
                <DsTextField
                  {...params}
                  name={field.ticketPersonMap || fieldKey}
                  label={field.attributeName}
                  required={field.required}
                  error={!!fieldState.error}
                  helperText={renderErrorContent(fieldState.error?.message)}
                  inputProps={{ ...params.inputProps, autoComplete: "new-password" }}
                />
              )}
            />
          );
        }}
      />
    );
  };

  const renderField = (ticket: Ticket, field: TicketPersonFormField) => {
    if (field.ticketPersonMap === "Phone")
      return renderPhoneField(ticket, field);
    if (field.ticketPersonMap === "Country")
      return renderCountrySelectField(ticket, field);
    if (field.ticketPersonMap === "City")
      return renderCitySelectField(ticket, field);

    switch (field.dataTypeCode) {
      case DataTypeCode.dropdown:
        return renderDropdownField(ticket, field);
      case DataTypeCode.text:
        return renderTextField(ticket, field, "text");
      case DataTypeCode.number:
        return renderTextField(ticket, field, "number");
      case DataTypeCode.checkbox:
        return renderCheckboxField(ticket, field);
      case DataTypeCode.date:
        return renderDateField(ticket, field);
      case DataTypeCode.warningMessage:
        return field.content ? (
          <WarningInfo
            content={field.content}
            isUnpaidServiceFee={false}
            isUnpaidDonation={false}
            isDelayedTicketDelivery={false}
            hasPendingServiceFeePayment={false}
            isGovWalletInfo={false}
            orderId={undefined}
          />
        ) : null;
      default:
        return null;
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Section id="ticket-info-section">
      <Title>{r.TicketInformation}</Title>
      {tickets
        .filter(
          (t) =>
            hasPerTicketProducts || !t.formFields || t.formFields.length > 0,
        )
        .map((ticket) => {
          const ticketFields = getTicketDisplayFields(ticket);
          const isExpanded = expandedIds.has(ticket.id);
          return (
        <MuiAccordion
          key={ticket.id}
          id={`ticket-accordion-${ticket.id}`}
          expanded={isExpanded}
          onChange={() => toggle(ticket.id)}
        >
          <MuiAccordionSummary expandIcon={<ArrowDown />}>
            <StatusIcon>
              {isTicketFormComplete(ticket, ticketFields) ? <Completed /> : <NonCompleted />}
            </StatusIcon>
            <AccordionTitleContainer>
              <ItemContent>
                {ticket.discount.name ? (
                  <TicketNameWithDiscount
                    name={ticket.priceListItem.name}
                    discountName={ticket.discount.name}
                  />
                ) : (
                  <ItemTitle>{ticket.priceListItem.name}</ItemTitle>
                )}
              </ItemContent>
              {ticket.seat?.isNumbered && (
                <TicketSection>
                  Section {ticket.seat.section} - {ticket.seat.row}/
                  {ticket.seat.number}
                </TicketSection>
              )}
            </AccordionTitleContainer>
          </MuiAccordionSummary>

          <MuiAccordionDetails>
            <AccordionBody $hasFields={ticketFields.length > 0}>
              {ticketFields.length > 0 && (
                <Grid>
                  {ticketFields.map((field) => {
                    // KOK: hide conditional fields until age >= 14
                    if (
                      isKokEvent &&
                      field.ticketPersonMap &&
                      KOK_AGE_CONDITIONAL_MAPS.has(field.ticketPersonMap) &&
                      !getIsKokAgeEligible(ticket.id)
                    )
                      return null;

                    // Olympiacos: hide the fancard on zero-price and kids-discount tickets
                    if (
                      field.ticketPersonMap === "SportsCard" &&
                      isOlympiacosFancardWaived(ticket, isOlympiacosEvent)
                    )
                      return null;
                    
                    // Fields not required for free tickets are hidden entirely on a zero-price ticket
                    if (field.notRequiredForFreeTickets && ticket.price === 0)
                      return null;

                    const Wrapper = FullWidth;
                    return (
                      <Wrapper key={field.dynamicAttributeId}>
                        {renderField(ticket, field)}
                      </Wrapper>
                    );
                  })}
                </Grid>
              )}
              {hasPerTicketProducts && isExpanded && (
                <MemoProducts currency={currency} ticketId={ticket.id} />
              )}
            </AccordionBody>
          </MuiAccordionDetails>
        </MuiAccordion>
          );
        })}
    </Section>
  );
}
